import React, { useState, useMemo, useEffect } from 'react';
import { 
  Settings, 
  Shield, 
  MapPin, 
  Stethoscope,
  Building, 
  Calendar,
  User, 
  Bell 
} from 'lucide-react';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { useDashboardHeaderTabs } from '../layout/DashboardHeaderContext';
import { useModeratorPermissions } from './hooks/useModeratorPermissions';
import { useModeratorData } from './hooks/useModeratorData';
import { OverviewSection } from './sections/OverviewSection';
import { InsurancesSection } from './sections/InsurancesSection';
import { LocationsSection } from './sections/LocationsSection';
import { ConditionsSection } from './sections/ConditionsSection';
import { AppointmentDetailsSection } from './sections/AppointmentDetailsSection';
import { ProvidersSection } from './sections/ProvidersSection';
import { EnhancedCustomerManagement } from './EnhancedCustomerManagement';
import { BulletinsBoard } from './BulletinsBoard';

export type TabId = 'overview' | 'insurances' | 'locations' | 'conditions' | 'customers' | 'appointmentDetails' | 'providers' | 'bulletins';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const BASE_TABS: TabConfig[] = [
  { id: 'overview', label: 'Overview', icon: Settings },
  { id: 'insurances', label: 'Insurances', icon: Shield },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'conditions', label: 'Conditions', icon: Stethoscope },
  { id: 'customers', label: 'Customers', icon: Building },
  { id: 'appointmentDetails', label: 'Appointment Details', icon: Calendar },
  { id: 'providers', label: 'Providers', icon: User },
];

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div 
        className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
        style={{ borderColor: 'var(--color-accent)' }} 
      />
      <p style={{ color: 'var(--color-text-secondary)' }}>Loading moderator data...</p>
    </div>
  </div>
);

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div className="text-red-500 mb-4">
        <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p style={{ color: 'var(--color-error)' }}>Error loading moderator data</p>
      <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
        {error.message}
      </p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded-lg"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'white'
        }}
      >
        Retry
      </button>
    </div>
  </div>
);

export const ModeratorDashboard: React.FC = () => {
  const { settings } = useApplicationSettings();
  const setHeaderTabs = useDashboardHeaderTabs();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const moderatorData = useModeratorData();
  const { canManageConditions, canAccessAppointmentDetails } = useModeratorPermissions();

  const tabs = useMemo(() => {
    const visibleBaseTabs = BASE_TABS.filter((tab) => {
      if (tab.id === 'conditions') return canManageConditions;
      if (tab.id === 'appointmentDetails') return canAccessAppointmentDetails;
      return true;
    });
    const bulletinsTab: TabConfig = { id: 'bulletins', label: 'Bulletins', icon: Bell };
    return settings?.bulletinsEnabled 
      ? [...visibleBaseTabs, bulletinsTab]
      : visibleBaseTabs;
  }, [settings?.bulletinsEnabled, canManageConditions, canAccessAppointmentDetails]);

  useEffect(() => {
    if (!canManageConditions && activeTab === 'conditions') {
      setActiveTab('overview');
    }
    if (!canAccessAppointmentDetails && activeTab === 'appointmentDetails') {
      setActiveTab('overview');
    }
  }, [canManageConditions, canAccessAppointmentDetails, activeTab]);

  const tabContentMap: Record<TabId, React.ReactNode> = useMemo(() => ({
    overview: (
      <OverviewSection
        insurances={moderatorData.insurances}
        conditions={moderatorData.conditions}
        providers={moderatorData.providers}
        locations={moderatorData.locations}
      />
    ),
    insurances: <InsurancesSection />,
    locations: <LocationsSection />,
    conditions: <ConditionsSection />,
    customers: <EnhancedCustomerManagement />,
    appointmentDetails: <AppointmentDetailsSection />,
    providers: <ProvidersSection />,
    bulletins: settings?.bulletinsEnabled ? <BulletinsBoard /> : null,
  }), [moderatorData.insurances, moderatorData.conditions, moderatorData.providers, moderatorData.locations, settings?.bulletinsEnabled]);

  // Register tab bar with Layout header (must run before any conditional return to satisfy Rules of Hooks)
  useEffect(() => {
    if (!setHeaderTabs) return;
    if (moderatorData.loading || moderatorData.error) {
      setHeaderTabs(null);
      return;
    }
    const tabBar = (
      <>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center space-x-2 py-2.5 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap"
              style={{
                borderBottomColor: isActive ? 'var(--color-accent)' : 'transparent',
                color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                  e.currentTarget.style.borderBottomColor = 'var(--color-border)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                  e.currentTarget.style.borderBottomColor = 'transparent';
                }
              }}
            >
              <Icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </>
    );
    setHeaderTabs(tabBar);
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, tabs, activeTab, moderatorData.loading, moderatorData.error]);

  if (moderatorData.loading) {
    return <LoadingState />;
  }

  if (moderatorData.error) {
    return <ErrorState error={moderatorData.error} onRetry={moderatorData.refetch} />;
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div 
          className="rounded-lg shadow-sm border p-6"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          {tabContentMap[activeTab] || tabContentMap.overview}
        </div>
      </div>
    </div>
  );
};

